import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Activity, Trophy, Users2, ShieldCheck } from 'lucide-react';

const benefits = [
  {
    icon: Activity,
    title: 'DESARROLLO FÍSICO Y MENTAL',
    description: 'Mejora de la condición física, coordinación, concentración y bienestar emocional a través del entrenamiento deportivo.',
    gradient: 'from-orange-500 to-amber-500'
  },
  {
    icon: Trophy,
    title: 'OPORTUNIDADES DEPORTIVAS',
    description: 'Participación en torneos, competencias regionales y posibilidades de crecimiento en el ámbito deportivo.',
    gradient: 'from-amber-500 to-yellow-500'
  },
  {
    icon: Users2,
    title: 'INTEGRACIÓN SOCIAL',
    description: 'Espacios de convivencia, nuevas amistades y desarrollo de habilidades sociales en un ambiente positivo.',
    gradient: 'from-orange-600 to-orange-500'
  },
  {
    icon: ShieldCheck,
    title: 'PREVENCIÓN DE RIESGOS',
    description: 'Alternativas constructivas que alejan a los jóvenes de situaciones de riesgo social y promueven estilos de vida saludables.',
    gradient: 'from-amber-600 to-amber-500'
  }
];

export function BenefitsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="beneficios" 
      className="py-24 px-4 sm:px-6 lg:px-8 bg-zinc-950"
      ref={ref}
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-500 font-semibold tracking-widest text-sm">ÚNETE A NOSOTROS</span>
          <h2 
            className="text-4xl sm:text-5xl font-extrabold text-white mt-2 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="benefits-title"
          >
            BENEFICIOS
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Formar parte de Jotuns Basketball Club es una experiencia transformadora para los jóvenes.
          </p>
        </motion.div>

        {/* Benefits Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              className="group relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900/50 p-8 hover:border-orange-500/50 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`benefit-${index}`}
            >
              {/* Background gradient on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              
              <div className="relative z-10">
                <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-6`}>
                  <benefit.icon className="text-white" size={28} />
                </div>
                
                <h3 
                  className="text-2xl font-bold text-white mb-3"
                  style={{ fontFamily: 'Barlow Condensed' }}
                >
                  {benefit.title}
                </h3>
                
                <p className="text-zinc-400 leading-relaxed">
                  {benefit.description}
                </p>
              </div>

              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-orange-500/10 to-transparent" />
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <p className="text-zinc-400 mb-6 text-lg">
            ¿Quieres ser parte de esta familia deportiva?
          </p>
          <a
            href="https://wa.me/573114540684?text=Hola,%20me%20interesa%20información%20sobre%20Jotuns%20Basketball%20Club"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-8 py-4 rounded-lg text-white font-semibold text-lg inline-flex items-center gap-2"
            data-testid="benefits-cta"
          >
            Contáctanos por WhatsApp
          </a>
        </motion.div>
      </div>
    </section>
  );
}
